import * as Sentry from '@sentry/react';
import { v4 as uuidv4 } from 'uuid';

/**
 * Context data for roster upload workflows
 */
interface UploadContext {
  trace_id: string;
  source_type?: string; // image | pdf | text
  file_name?: string;
  file_size?: number;
  gcs_url?: string;
  roster_type?: string; // single_team | showcase
  operation?: string; // upload | assess | extract | field_mapping | team_selection | showcase_selection
  phase?: string; // Sub-operation detail
  extraction_id?: string;
  start_time?: string;
  duration_ms?: number;
  prospect_count?: number;
  team_count?: number;
  [key: string]: string | number | undefined;
}

/**
 * Error capture context
 */
interface ErrorContext {
  operation: string;
  phase: string;
  level?: 'error' | 'warning';
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  fingerprint?: string[];
}

/**
 * User context from authentication
 */
interface UserContext {
  id: string;
  email?: string;
  role?: string;
  orgId?: string;
}

/**
 * SentryService provides centralized error logging and context management
 * for the roster upload workflow, mirroring the mobile app's implementation.
 */
export class SentryService {
  /**
   * Generate unique trace ID for roster upload workflows.
   * Links all operations (upload, processing, updates) together.
   */
  static generateTraceId(): string {
    return uuidv4();
  }

  /**
   * Set roster upload context.
   * Used throughout the roster upload lifecycle.
   */
  static setUploadContext(data: UploadContext): void {
    Sentry.setContext('roster_upload', data);

    // Set tags for easy filtering
    if (data.trace_id) {
      Sentry.setTag('upload_trace_id', data.trace_id);
    }
    if (data.source_type) {
      Sentry.setTag('source_type', data.source_type);
    }
    if (data.roster_type) {
      Sentry.setTag('roster_type', data.roster_type);
    }
    if (data.operation) {
      Sentry.setTag('operation', data.operation);
    }
  }

  /**
   * Add breadcrumb for roster upload operations.
   */
  static addUploadBreadcrumb(
    message: string,
    data?: Record<string, unknown>
  ): void {
    Sentry.addBreadcrumb({
      category: 'roster_upload',
      message,
      level: 'info',
      data,
    });
  }

  /**
   * Capture roster upload operation error with rich context.
   * Uses captureMessage (like mobile app) for API errors without stack traces.
   */
  static captureError(
    error: Error | string,
    context: ErrorContext
  ): void {
    const errorMessage = typeof error === 'string' ? error : error.message;

    Sentry.captureMessage(errorMessage, {
      level: context.level || 'error',
      tags: {
        error_category: context.tags?.error_category || 'extraction_failed',
        operation: context.operation,
        phase: context.phase,
        ...context.tags,
      },
      extra: context.extra,
      fingerprint: context.fingerprint || [
        'roster-upload',
        context.operation,
        context.phase,
      ],
    });
  }

  /**
   * Set user context from authentication.
   */
  static setUser(user: UserContext): void {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      role: user.role,
      organization_id: user.orgId,
    });
  }

  /**
   * Clear context (on logout or workflow completion).
   */
  static clearContext(): void {
    Sentry.setContext('roster_upload', null);
    Sentry.setUser(null);
  }
}
